import React, { useEffect, useMemo, useRef, useState } from 'react';
import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DoneAll from '@mui/icons-material/DoneAll';
import Undo from '@mui/icons-material/Undo';
import Visibility from '@mui/icons-material/Visibility';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Notifier } from '../../system/services/notificationManager';
import { HierarchyEditor } from './conductor-commissioning/hierarchy-editor';
import { DeviceMappingEditor } from './conductor-commissioning/device-mapping-editor';
import { ReviewAndProvision } from './conductor-commissioning/review-and-provision';
import { CommissioningDraft } from './conductor-commissioning/types';
import {
  useCommitConductorDiscoveryMappingsMutation,
  useConductorBuildingUnitsQuery,
  useConductorCommissioningDraftQuery,
  useConductorDiscoveryRunQuery,
  useConductorDiscoveryStatusUpdatedSubscription,
  useCreateConductorCommissioningDraftMutation,
  useFinalizeConductorCommissioningDraftMutation,
  useRetryConductorCommissioningCommitMutation,
  useUpdateConductorCommissioningDraftMutation,
} from '../../../types/generated-types';

type Candidate = {
  discoveredDeviceId: string;
  displayName: string;
  module: string;
  deviceType: string;
  supportedMetrics: string[];
  supportedCommands: string[];
};

type UnitOption = {
  _id: string;
  name: string;
  type?: string | null;
  propertyId?: string | null;
  buildingId?: string | null;
  floorId?: string | null;
  building?: { name?: string | null } | null;
  floor?: { name?: string | null } | null;
};

type MappingDraft = {
  discoveredDeviceId: string;
  superDeviceId: string;
  deviceType: string;
  propertyId: string;
  buildingId?: string;
  floorId?: string;
  unitId: string;
  zoneId?: string;
  panelId?: string;
  enabledMetrics: string[];
  enabledCommands: string[];
  confidence?: number;
  reason?: string;
  agreement?: string;
  proposedDeviceName?: string;
  nameReason?: string;
  modelVotes?: unknown[];
  reviewStatus?: ReviewStatus;
};

type ReviewStatus = 'pending' | 'accepted' | 'needsReview' | 'ignored';

type MappingView =
  | 'all'
  | 'needsAttention'
  | 'proposed'
  | 'accepted'
  | 'ignored';

const HIGH_CONFIDENCE_THRESHOLD = 0.9;

function buildDraft(candidate: Candidate, propertyId?: string): MappingDraft {
  return {
    discoveredDeviceId: candidate.discoveredDeviceId,
    superDeviceId: candidate.discoveredDeviceId,
    deviceType: candidate.deviceType || 'ConductorGenericSensor',
    propertyId: propertyId || 'conductor-prototype-property',
    unitId: '',
    enabledMetrics: candidate.supportedMetrics ?? [],
    enabledCommands: candidate.supportedCommands ?? [],
  };
}

function candidateFromMapping(mapping: MappingDraft): Candidate {
  return {
    discoveredDeviceId: mapping.discoveredDeviceId,
    displayName: mapping.proposedDeviceName ?? mapping.discoveredDeviceId,
    module: '',
    deviceType: mapping.deviceType,
    supportedMetrics: mapping.enabledMetrics ?? [],
    supportedCommands: mapping.enabledCommands ?? [],
  };
}

function unitLabel(unit: UnitOption) {
  const location = [unit.building?.name, unit.floor?.name]
    .filter(Boolean)
    .join(' / ');
  return location ? `${unit.name} (${location})` : unit.name;
}

function unitSearchText(unit: UnitOption) {
  return [unit.name, unit.type, unit.building?.name, unit.floor?.name, unit._id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function isCentralEquipmentDevice(deviceType?: string) {
  return ['Chiller', 'Controller', 'PowerMonitor'].includes(deviceType || '');
}

function unitMatchesCandidate(unit: UnitOption, candidate?: Candidate) {
  if (isCentralEquipmentDevice(candidate?.deviceType)) {
    return unit.type === 'centralEquipment';
  }

  if (candidate?.deviceType === 'Thermostat') {
    return unit.type === 'residential' || unit.type === 'commonArea';
  }

  return true;
}

function findSuggestedUnit(candidate: Candidate, units: UnitOption[]) {
  const candidateUnits = units.filter((unit) =>
    unitMatchesCandidate(unit, candidate),
  );

  if (candidateUnits.length === 1) {
    return candidateUnits[0];
  }

  const candidateText = [
    candidate.displayName,
    candidate.discoveredDeviceId,
  ].join(' ');
  const apartmentMatch = candidateText.match(/APT-(\d{3})/i);
  if (apartmentMatch) {
    const unitName = `Unit ${apartmentMatch[1]}`.toLowerCase();
    return candidateUnits.find(
      (unit) =>
        unit.type === 'residential' &&
        unit.name?.trim().toLowerCase() === unitName,
    );
  }

  const commonMappings = [
    [/COMMON-LOBBY|LOBBY/i, 'Lobby'],
    [/COMMON-FITNESS|FITNESS/i, 'Fitness Room'],
    [/COMMON-COMMUNITY|COMMUNITY/i, 'Community Room'],
    [/COMMON-CORRIDOR-L01|CORRIDOR/i, 'Level 1 Corridor'],
  ] as const;
  const commonMatch = commonMappings.find(([pattern]) =>
    pattern.test(candidateText),
  );
  if (commonMatch) {
    const [, unitName] = commonMatch;
    return candidateUnits.find(
      (unit) =>
        unit.type === 'commonArea' &&
        unit.name?.trim().toLowerCase() === unitName.toLowerCase(),
    );
  }

  if (candidate.deviceType === 'ConductorGenericSensor') {
    return candidateUnits.find((unit) => unit.name === 'Unit 101');
  }

  return undefined;
}

function conductorZoneId(sourceId: string, discoveredDeviceId: string) {
  return `${sourceId}-${discoveredDeviceId}-zone`;
}

function conductorPanelId(sourceId: string, discoveredDeviceId: string) {
  return `${sourceId}-${discoveredDeviceId}-panel`;
}

function mappingUnitId(mapping: Partial<MappingDraft>) {
  return typeof mapping.unitId === 'string' ? mapping.unitId : '';
}

function countLabel(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function reviewStatus(draft: MappingDraft): ReviewStatus {
  if (draft.reviewStatus) {
    return draft.reviewStatus;
  }
  return draft.unitId ? 'pending' : 'needsReview';
}

function reviewStatusLabel(status: ReviewStatus) {
  switch (status) {
    case 'accepted':
      return 'Accepted';
    case 'ignored':
      return 'Ignored';
    case 'needsReview':
      return 'Needs attention';
    case 'pending':
    default:
      return 'Proposed';
  }
}

function reviewStatusColor(status: ReviewStatus) {
  switch (status) {
    case 'accepted':
      return 'success';
    case 'ignored':
      return 'default';
    case 'needsReview':
      return 'warning';
    case 'pending':
    default:
      return 'info';
  }
}

function mappingEvidenceText(draft: MappingDraft) {
  if (draft.reason) {
    const confidence =
      draft.confidence != null
        ? ` (${Math.round(draft.confidence * 100)}%${
            draft.agreement ? `, ${draft.agreement}` : ''
          })`
        : '';
    return `Suggested mapping${confidence}: ${draft.reason}`;
  }

  if (draft.unitId) {
    return 'Suggested from discovered device name, type, and available destination list.';
  }

  return 'No confident destination yet.';
}

function proposedNameEvidenceText(draft: MappingDraft) {
  if (draft.nameReason) {
    return draft.nameReason;
  }
  if (draft.proposedDeviceName) {
    return 'Suggested from device type and proposed destination.';
  }
  return 'No display name suggestion yet.';
}

function commissioningDraftPatch(draft: CommissioningDraft) {
  const evidence = (values: Array<{ source: string; value: string }>) =>
    values.map(({ source, value }) => ({ source, value }));
  return {
    mode: draft.mode,
    floors: draft.floors.map(
      ({
        key,
        name,
        number,
        existingFloorId,
        confidence,
        evidence: proof,
      }) => ({
        key,
        name,
        number: number ?? undefined,
        existingFloorId: existingFloorId ?? undefined,
        confidence,
        evidence: evidence(proof),
      }),
    ),
    units: draft.units.map(
      ({
        key,
        name,
        type,
        floorKey,
        existingUnitId,
        confidence,
        evidence: proof,
      }) => ({
        key,
        name,
        type,
        floorKey: floorKey ?? undefined,
        existingUnitId: existingUnitId ?? undefined,
        confidence,
        evidence: evidence(proof),
      }),
    ),
    devices: draft.devices.map(
      ({
        key,
        discoveredDeviceId,
        originalLabel,
        proposedName,
        unitKey,
        deviceType,
        supportedMetrics,
        supportedCommands,
        enabledMetrics,
        enabledCommands,
        ignored,
        confidence,
        evidence: proof,
      }) => ({
        key,
        discoveredDeviceId,
        originalLabel,
        proposedName,
        unitKey: unitKey ?? undefined,
        deviceType,
        supportedMetrics,
        supportedCommands,
        enabledMetrics,
        enabledCommands,
        ignored: !!ignored,
        confidence,
        evidence: evidence(proof),
      }),
    ),
    needsReview: draft.needsReview,
  };
}

interface ConductorCommissioningPanelProps {
  buildingId?: string | null;
  propertyId?: string | null;
  sourceId: string;
}

export function ConductorCommissioningPanel(
  props: ConductorCommissioningPanelProps,
) {
  const { sourceId } = props;
  const discoveryQuery = useConductorDiscoveryRunQuery({
    fetchPolicy: 'network-only',
    variables: { sourceId },
  });
  const { data: subscriptionData } =
    useConductorDiscoveryStatusUpdatedSubscription({
      skip: !sourceId,
      variables: { sourceId },
    });
  const commissioningRunId =
    subscriptionData?.conductorDiscoveryStatusUpdated?.runId ??
    discoveryQuery.data?.conductorDiscoveryRun?.runId;
  return (
    <ConductorCommissioningRunPanel
      key={JSON.stringify([sourceId, commissioningRunId])}
      {...props}
      discoveryQuery={discoveryQuery}
      subscriptionData={subscriptionData}
      commissioningRunId={commissioningRunId}
    />
  );
}

// A replacement discovery owns new edit and request state. Late callbacks from
// the unmounted run must not replace its draft or announce stale completion.
function ConductorCommissioningRunPanel({
  buildingId,
  propertyId,
  sourceId,
  discoveryQuery,
  subscriptionData,
  commissioningRunId,
}: ConductorCommissioningPanelProps & {
  discoveryQuery: ReturnType<typeof useConductorDiscoveryRunQuery>;
  subscriptionData: ReturnType<
    typeof useConductorDiscoveryStatusUpdatedSubscription
  >['data'];
  commissioningRunId?: string;
}) {
  const { data, error, loading, refetch } = discoveryQuery;
  const commissioningRunActive = useRef(true);
  useEffect(() => {
    commissioningRunActive.current = true;
    return () => {
      commissioningRunActive.current = false;
    };
  }, []);
  const {
    data: unitData,
    error: unitError,
    loading: unitsLoading,
  } = useConductorBuildingUnitsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !buildingId,
    variables: { buildingId: buildingId ?? '' },
  });
  const [commitMappings, { loading: committing }] =
    useCommitConductorDiscoveryMappingsMutation();
  const {
    data: commissioningData,
    loading: commissioningDraftLoading,
    refetch: refetchCommissioningDraft,
  } = useConductorCommissioningDraftQuery({
    fetchPolicy: 'network-only',
    skip: !sourceId || !commissioningRunId,
    variables: { sourceId, runId: commissioningRunId },
  });
  const [createCommissioningDraft, { loading: creatingDraft }] =
    useCreateConductorCommissioningDraftMutation();
  const [updateCommissioningDraft, { loading: savingDraft }] =
    useUpdateConductorCommissioningDraftMutation();
  const [finalizeCommissioningDraft, { loading: provisioningDraft }] =
    useFinalizeConductorCommissioningDraftMutation();
  const [retryCommissioningCommit, { loading: retryingCommit }] =
    useRetryConductorCommissioningCommitMutation();
  const discoveryStatus = subscriptionData?.conductorDiscoveryStatusUpdated;
  const discoveryRun = data?.conductorDiscoveryRun ?? null;
  const availableDiscoveryResults =
    discoveryStatus?.results ?? discoveryRun ?? null;
  const discovery =
    discoveryStatus && !discoveryStatus.results
      ? null
      : availableDiscoveryResults;
  const proposedMappings = useMemo(
    () =>
      (discoveryStatus?.proposedMappings ??
        discoveryRun?.proposedMappings ??
        []) as MappingDraft[],
    [discoveryRun, discoveryStatus],
  );
  const unallocatedMappings = useMemo(
    () =>
      (discoveryStatus?.unallocated ??
        discoveryRun?.unallocated ??
        []) as Partial<MappingDraft>[],
    [discoveryRun, discoveryStatus],
  );
  const candidates = useMemo(
    () => (discovery?.candidates ?? []) as Candidate[],
    [discovery],
  );
  const units = useMemo(
    () => (unitData?.units ?? []) as UnitOption[],
    [unitData],
  );
  const [drafts, setDrafts] = useState<MappingDraft[]>([]);
  const [committed, setCommitted] = useState(false);
  const [view, setView] = useState<MappingView>('all');
  const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null);
  const [commissioningDraft, setCommissioningDraft] =
    useState<CommissioningDraft | null>(null);
  const [commissioningStage, setCommissioningStage] = useState(0);
  const [commissioningDraftDirty, setCommissioningDraftDirty] = useState(false);
  const [commissioningSaving, setCommissioningSaving] = useState(false);
  const [commissioningSaveError, setCommissioningSaveError] = useState(false);
  const commissioningSaveInFlight = useRef(false);
  const [commissioningCommitting, setCommissioningCommitting] = useState(false);
  const commissioningCommitInFlight = useRef(false);

  useEffect(() => {
    const nextDraft = commissioningData?.conductorCommissioningDraft;
    if (
      nextDraft &&
      nextDraft.sourceId === sourceId &&
      nextDraft.runId === commissioningRunId &&
      !commissioningDraftDirty
    ) {
      setCommissioningDraft((current) =>
        current &&
        current._id === nextDraft._id &&
        current.revision === nextDraft.revision &&
        current.status === nextDraft.status
          ? current
          : (nextDraft as CommissioningDraft),
      );
    }
  }, [
    commissioningData,
    commissioningDraftDirty,
    sourceId,
    commissioningRunId,
  ]);

  useEffect(() => {
    if (
      !commissioningDraft ||
      !commissioningDraftDirty ||
      commissioningSaving ||
      commissioningSaveError ||
      commissioningDraft.status !== 'draft'
    )
      return;
    const saveTimer = window.setTimeout(async () => {
      if (commissioningSaveInFlight.current) return;
      commissioningSaveInFlight.current = true;
      setCommissioningSaving(true);
      try {
        const response = await updateCommissioningDraft({
          variables: {
            sourceId,
            draftId: commissioningDraft._id,
            expectedRevision: commissioningDraft.revision,
            expectedDiscoveryFingerprint:
              commissioningDraft.discoveryFingerprint,
            patch: commissioningDraftPatch(commissioningDraft),
          },
        });
        if (!commissioningRunActive.current) return;
        const saved = response.data?.updateConductorCommissioningDraft;
        if (!saved)
          throw new Error('Commissioning draft save returned no draft');
        setCommissioningDraft(saved as CommissioningDraft);
        setCommissioningDraftDirty(false);
      } catch (saveError) {
        if (!commissioningRunActive.current) return;
        setCommissioningSaveError(true);
        Notifier.error(
          'Unable to save the commissioning draft. Your edits are retained; reload the draft before continuing.',
        );
      } finally {
        if (commissioningRunActive.current) {
          commissioningSaveInFlight.current = false;
          setCommissioningSaving(false);
        }
      }
    }, 500);
    return () => window.clearTimeout(saveTimer);
  }, [
    commissioningDraft,
    commissioningDraftDirty,
    commissioningSaving,
    commissioningSaveError,
    sourceId,
    updateCommissioningDraft,
  ]);

  useEffect(() => {
    if (drafts.length || !candidates.length || (buildingId && !units.length)) {
      return;
    }

    if (proposedMappings.length || unallocatedMappings.length) {
      const seenDeviceIds = new Set<string>();
      const nextDrafts: MappingDraft[] = [];

      const addMappingDraft = (
        mapping: Partial<MappingDraft>,
        nextReviewStatus: ReviewStatus,
      ) => {
        const discoveredDeviceId = String(mapping.discoveredDeviceId ?? '');
        if (!discoveredDeviceId || seenDeviceIds.has(discoveredDeviceId)) {
          return;
        }

        seenDeviceIds.add(discoveredDeviceId);
        const unitId = mappingUnitId(mapping);
        const candidate = candidates.find(
          (item) => item.discoveredDeviceId === discoveredDeviceId,
        );
        const draftCandidate =
          candidate ?? candidateFromMapping(mapping as MappingDraft);
        const unit = units.find((item) => item._id === unitId);
        const centralEquipment = isCentralEquipmentDevice(
          mapping.deviceType || candidate?.deviceType,
        );

        nextDrafts.push({
          ...buildDraft(draftCandidate, propertyId ?? undefined),
          ...mapping,
          discoveredDeviceId,
          unitId,
          reviewStatus: nextReviewStatus,
          propertyId:
            unit?.propertyId ?? propertyId ?? mapping.propertyId ?? '',
          buildingId: unit?.buildingId ?? mapping.buildingId,
          floorId: unit?.floorId ?? mapping.floorId,
          ...(unit
            ? centralEquipment
              ? {
                  panelId:
                    mapping.panelId ||
                    conductorPanelId(sourceId, discoveredDeviceId),
                  zoneId: undefined,
                }
              : {
                  zoneId:
                    mapping.zoneId ||
                    conductorZoneId(sourceId, discoveredDeviceId),
                  panelId: undefined,
                }
            : {}),
        } as MappingDraft);
      };

      proposedMappings.forEach((mapping) => {
        addMappingDraft(
          mapping,
          mappingUnitId(mapping) ? 'pending' : 'needsReview',
        );
      });
      unallocatedMappings.forEach((mapping) => {
        addMappingDraft(mapping, 'needsReview');
      });

      candidates.forEach((candidate) => {
        if (seenDeviceIds.has(candidate.discoveredDeviceId)) {
          return;
        }
        const draft = buildDraft(candidate, propertyId ?? undefined);
        const suggestedUnit = findSuggestedUnit(candidate, units);
        if (suggestedUnit) {
          const unit = suggestedUnit;
          const centralEquipment = isCentralEquipmentDevice(
            candidate.deviceType,
          );
          nextDrafts.push({
            ...draft,
            propertyId: unit.propertyId ?? draft.propertyId,
            buildingId: unit.buildingId ?? undefined,
            floorId: unit.floorId ?? undefined,
            unitId: unit._id,
            reviewStatus: 'pending',
            ...(centralEquipment
              ? {
                  panelId: conductorPanelId(
                    sourceId,
                    candidate.discoveredDeviceId,
                  ),
                }
              : {
                  zoneId: conductorZoneId(
                    sourceId,
                    candidate.discoveredDeviceId,
                  ),
                }),
          });
          return;
        }
        nextDrafts.push({ ...draft, reviewStatus: 'needsReview' });
      });

      setDrafts(nextDrafts);
      return;
    }

    setDrafts(
      candidates.map((candidate) => {
        const draft = buildDraft(candidate, propertyId ?? undefined);
        const suggestedUnit = findSuggestedUnit(candidate, units);
        if (suggestedUnit) {
          const unit = suggestedUnit;
          const centralEquipment = isCentralEquipmentDevice(
            candidate.deviceType,
          );
          return {
            ...draft,
            propertyId: unit.propertyId ?? draft.propertyId,
            buildingId: unit.buildingId ?? undefined,
            floorId: unit.floorId ?? undefined,
            unitId: unit._id,
            reviewStatus: 'pending' as ReviewStatus,
            ...(centralEquipment
              ? {
                  panelId: conductorPanelId(
                    sourceId,
                    candidate.discoveredDeviceId,
                  ),
                }
              : {
                  zoneId: conductorZoneId(
                    sourceId,
                    candidate.discoveredDeviceId,
                  ),
                }),
          };
        }
        return { ...draft, reviewStatus: 'needsReview' as ReviewStatus };
      }),
    );
  }, [
    candidates,
    buildingId,
    drafts.length,
    propertyId,
    proposedMappings,
    sourceId,
    unallocatedMappings,
    units,
  ]);

  if (error) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Initialize this Conductor source to start BACnet discovery.
      </Alert>
    );
  }

  const activeStatus = discoveryStatus ?? discovery;
  if (loading && !activeStatus) {
    return (
      <Box sx={{ m: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!activeStatus) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No Conductor discovery run has been started for this source.
      </Alert>
    );
  }

  const complete = activeStatus.status === 'complete';
  const progress = activeStatus.progressPercent ?? 0;

  const prepareCommissioningDraft = async () => {
    try {
      const response = await createCommissioningDraft({
        variables: { sourceId, runId: activeStatus.runId },
      });
      if (!commissioningRunActive.current) return;
      const created = response.data?.createConductorCommissioningDraft;
      if (created) setCommissioningDraft(created as CommissioningDraft);
    } catch (draftError) {
      if (!commissioningRunActive.current) return;
      Notifier.error('Unable to prepare the Conductor commissioning draft.');
    }
  };

  const changeCommissioningDraft = (nextDraft: CommissioningDraft) => {
    if (
      commissioningDraft?.status !== 'draft' ||
      savingDraft ||
      provisioningDraft ||
      retryingCommit ||
      commissioningSaveInFlight.current ||
      commissioningCommitInFlight.current ||
      commissioningSaveError
    )
      return;
    setCommissioningDraft(nextDraft);
    setCommissioningDraftDirty(true);
  };

  const reloadCommissioningDraft = async () => {
    try {
      const response = await refetchCommissioningDraft();
      if (!commissioningRunActive.current) return;
      const reloaded = response.data?.conductorCommissioningDraft;
      if (!reloaded)
        throw new Error('Commissioning draft reload returned no draft');
      setCommissioningDraft(reloaded as CommissioningDraft);
      setCommissioningDraftDirty(false);
      setCommissioningSaveError(false);
    } catch (reloadError) {
      if (!commissioningRunActive.current) return;
      Notifier.error(
        'Unable to reload the commissioning draft. Your edits are retained.',
      );
    }
  };

  const provisionHierarchy = async () => {
    if (
      !commissioningDraft ||
      commissioningDraftDirty ||
      savingDraft ||
      provisioningDraft ||
      retryingCommit ||
      commissioningSaveInFlight.current ||
      commissioningCommitInFlight.current ||
      commissioningSaveError
    )
      return;
    commissioningCommitInFlight.current = true;
    setCommissioningCommitting(true);
    try {
      const response = await finalizeCommissioningDraft({
        variables: {
          sourceId,
          draftId: commissioningDraft._id,
          expectedRevision: commissioningDraft.revision,
          expectedDiscoveryFingerprint: commissioningDraft.discoveryFingerprint,
        },
      });
      if (!commissioningRunActive.current) return;
      const finalized = response.data?.finalizeConductorCommissioningDraft;
      if (finalized) setCommissioningDraft(finalized as CommissioningDraft);
      if (finalized?.status === 'complete') {
        Notifier.success('Conductor commissioning is complete.');
      } else if (finalized?.status === 'awaitingConductor') {
        Notifier.warn(
          'Local hierarchy provisioned; Conductor acknowledgement is pending.',
        );
      } else {
        Notifier.error(
          'Commissioning did not complete. Reload the draft before continuing.',
        );
      }
    } catch (provisionError) {
      if (!commissioningRunActive.current) return;
      Notifier.error('Unable to provision the Conductor hierarchy.');
    } finally {
      if (commissioningRunActive.current) {
        commissioningCommitInFlight.current = false;
        setCommissioningCommitting(false);
      }
    }
  };

  const retryConductorAcknowledgement = async () => {
    if (
      !commissioningDraft ||
      savingDraft ||
      provisioningDraft ||
      retryingCommit ||
      commissioningSaveInFlight.current ||
      commissioningCommitInFlight.current
    )
      return;
    commissioningCommitInFlight.current = true;
    setCommissioningCommitting(true);
    try {
      const response = await retryCommissioningCommit({
        variables: { sourceId, draftId: commissioningDraft._id },
      });
      if (!commissioningRunActive.current) return;
      const retried = response.data?.retryConductorCommissioningCommit;
      if (retried) setCommissioningDraft(retried as CommissioningDraft);
      Notifier.success('Conductor acknowledged the commissioned hierarchy.');
    } catch (retryError) {
      if (!commissioningRunActive.current) return;
      Notifier.error('Conductor acknowledgement retry failed.');
    } finally {
      if (commissioningRunActive.current) {
        commissioningCommitInFlight.current = false;
        setCommissioningCommitting(false);
      }
    }
  };

  if (complete && commissioningDraftLoading && !commissioningDraft) {
    return (
      <Box sx={{ m: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (complete && commissioningDraft) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography variant="h6">Conductor Commissioning</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Review the inferred hierarchy and original controller labels before
          provisioning any entities.
        </Typography>
        {commissioningSaveError && (
          <Alert
            action={
              <Button onClick={reloadCommissioningDraft}>Reload draft</Button>
            }
            severity="error"
            sx={{ mb: 2 }}
          >
            Your unsaved edits are retained. Reloading discards those edits and
            loads the latest saved draft before you can continue.
          </Alert>
        )}
        <Tabs
          aria-label="Conductor commissioning stages"
          onChange={(_event, value) => setCommissioningStage(value)}
          value={commissioningStage}
        >
          <Tab label="Floors and Units" />
          <Tab label="Devices" />
          <Tab label="Review" />
        </Tabs>
        <Box sx={{ mt: 2 }}>
          {commissioningStage === 0 && (
            <HierarchyEditor
              draft={commissioningDraft}
              onChange={changeCommissioningDraft}
              readOnly={
                savingDraft ||
                commissioningSaving ||
                commissioningSaveError ||
                provisioningDraft ||
                retryingCommit ||
                commissioningCommitting
              }
            />
          )}
          {commissioningStage === 1 && (
            <DeviceMappingEditor
              draft={commissioningDraft}
              onChange={changeCommissioningDraft}
              readOnly={
                savingDraft ||
                commissioningSaving ||
                commissioningSaveError ||
                provisioningDraft ||
                retryingCommit ||
                commissioningCommitting
              }
            />
          )}
          {commissioningStage === 2 && (
            <ReviewAndProvision
              draft={commissioningDraft}
              loading={
                savingDraft ||
                commissioningSaving ||
                commissioningCommitting ||
                commissioningDraftDirty ||
                provisioningDraft ||
                retryingCommit
              }
              onProvision={provisionHierarchy}
              onRetry={retryConductorAcknowledgement}
            />
          )}
        </Box>
      </Box>
    );
  }

  const updateDraftUnit = (discoveredDeviceId: string, unit?: UnitOption) => {
    setDrafts((current) =>
      current.map((draft) => {
        if (draft.discoveredDeviceId !== discoveredDeviceId) {
          return draft;
        }

        const centralEquipment = isCentralEquipmentDevice(draft.deviceType);
        return {
          ...draft,
          propertyId: unit?.propertyId ?? propertyId ?? draft.propertyId,
          buildingId: unit?.buildingId ?? undefined,
          floorId: unit?.floorId ?? undefined,
          unitId: unit?._id ?? '',
          reviewStatus: unit ? 'accepted' : 'needsReview',
          zoneId:
            unit && !centralEquipment
              ? conductorZoneId(sourceId, discoveredDeviceId)
              : undefined,
          panelId:
            unit && centralEquipment
              ? conductorPanelId(sourceId, discoveredDeviceId)
              : undefined,
        };
      }),
    );
  };

  const updateDraftName = (
    discoveredDeviceId: string,
    proposedDeviceName: string,
  ) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.discoveredDeviceId === discoveredDeviceId
          ? { ...draft, proposedDeviceName }
          : draft,
      ),
    );
  };

  const acceptDraft = (discoveredDeviceId: string) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.discoveredDeviceId === discoveredDeviceId && draft.unitId
          ? { ...draft, reviewStatus: 'accepted' }
          : draft,
      ),
    );
  };

  const ignoreDraft = (discoveredDeviceId: string) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.discoveredDeviceId === discoveredDeviceId
          ? { ...draft, reviewStatus: 'ignored' }
          : draft,
      ),
    );
  };

  const restoreDraft = (discoveredDeviceId: string) => {
    setDrafts((current) =>
      current.map((draft) =>
        draft.discoveredDeviceId === discoveredDeviceId
          ? {
              ...draft,
              reviewStatus: draft.unitId ? 'pending' : 'needsReview',
            }
          : draft,
      ),
    );
  };

  const acceptHighConfidenceMappings = () => {
    setDrafts((current) =>
      current.map((draft) => {
        const status = reviewStatus(draft);
        if (
          status === 'pending' &&
          draft.unitId &&
          (draft.confidence ?? 0) >= HIGH_CONFIDENCE_THRESHOLD
        ) {
          return { ...draft, reviewStatus: 'accepted' };
        }
        return draft;
      }),
    );
  };

  const acceptAllProposedMappings = () => {
    setDrafts((current) =>
      current.map((draft) =>
        reviewStatus(draft) === 'pending' && draft.unitId
          ? { ...draft, reviewStatus: 'accepted' }
          : draft,
      ),
    );
  };

  const acceptedCount = drafts.filter(
    (draft) => reviewStatus(draft) === 'accepted',
  ).length;
  const ignoredCount = drafts.filter(
    (draft) => reviewStatus(draft) === 'ignored',
  ).length;
  const needsAttentionCount = drafts.filter(
    (draft) => reviewStatus(draft) === 'needsReview',
  ).length;
  const proposedCount = drafts.filter(
    (draft) => reviewStatus(draft) === 'pending',
  ).length;
  const highConfidencePendingCount = drafts.filter(
    (draft) =>
      reviewStatus(draft) === 'pending' &&
      draft.unitId &&
      (draft.confidence ?? 0) >= HIGH_CONFIDENCE_THRESHOLD,
  ).length;
  const pendingWithDestinationCount = drafts.filter(
    (draft) => reviewStatus(draft) === 'pending' && draft.unitId,
  ).length;
  const committableMappings = drafts.filter(
    (draft) => reviewStatus(draft) !== 'ignored' && draft.unitId,
  );
  const zoneCount = committableMappings.filter(
    (draft) => !isCentralEquipmentDevice(draft.deviceType),
  ).length;
  const panelCount = committableMappings.filter((draft) =>
    isCentralEquipmentDevice(draft.deviceType),
  ).length;
  const visibleDrafts = drafts.filter((draft) => {
    const status = reviewStatus(draft);
    switch (view) {
      case 'accepted':
        return status === 'accepted';
      case 'ignored':
        return status === 'ignored';
      case 'needsAttention':
        return status === 'needsReview';
      case 'proposed':
        return status === 'pending';
      case 'all':
      default:
        return true;
    }
  });

  const readyToCommit =
    complete &&
    drafts.length > 0 &&
    drafts.every((draft) =>
      ['accepted', 'ignored'].includes(reviewStatus(draft)),
    ) &&
    committableMappings.length > 0;

  const handleCommit = async () => {
    try {
      await commitMappings({
        variables: {
          sourceId,
          runId: activeStatus.runId,
          mappings: drafts.filter((draft) => reviewStatus(draft) !== 'ignored'),
        },
      });
      if (!commissioningRunActive.current) return;
      Notifier.success('Conductor mappings committed');
      setCommitted(true);
      await refetch();
    } catch {
      if (!commissioningRunActive.current) return;
      Notifier.error('Unable to commit Conductor mappings');
    }
  };

  const renderFilterButton = (
    nextView: MappingView,
    label: string,
    count: number,
  ) => (
    <Button
      key={nextView}
      onClick={() => setView(nextView)}
      size="small"
      variant={view === nextView ? 'contained' : 'outlined'}
    >
      {label} ({count})
    </Button>
  );

  return (
    <Box sx={{ m: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Conductor Discovery
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
            {activeStatus.message}
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>

        <Box
          aria-label="Conductor discovery summary"
          role="region"
          sx={{
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '4px',
            p: 1.5,
          }}
        >
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
            <Chip
              label={countLabel(
                candidates.length,
                'device found',
                'devices found',
              )}
            />
            <Chip color="info" label={`${proposedCount} proposed`} />
            <Chip
              color="warning"
              label={`${needsAttentionCount} needs attention`}
            />
            <Chip color="success" label={`${acceptedCount} accepted`} />
            <Chip label={`${ignoredCount} ignored`} />
          </Stack>
          <Typography variant="subtitle2">Commit preview</Typography>
          <Typography variant="body2">
            {countLabel(committableMappings.length, 'device')} will be created
            or updated across {countLabel(zoneCount, 'zone')} and{' '}
            {countLabel(panelCount, 'panel')}. {ignoredCount} ignored.
          </Typography>
        </Box>

        <Stack direction="row" flexWrap="wrap" gap={1}>
          {complete && !commissioningDraft && (
            <Button
              disabled={creatingDraft}
              onClick={prepareCommissioningDraft}
              size="small"
              variant="outlined"
            >
              Prepare commissioning draft
            </Button>
          )}
          <Button
            disabled={!highConfidencePendingCount}
            onClick={acceptHighConfidenceMappings}
            size="small"
            startIcon={<DoneAll />}
            variant="contained"
          >
            Accept all high-confidence
          </Button>
          <Button
            disabled={!pendingWithDestinationCount}
            onClick={acceptAllProposedMappings}
            size="small"
            startIcon={<DoneAll />}
            variant="outlined"
          >
            Accept all proposed
          </Button>
          {renderFilterButton(
            'needsAttention',
            'Needs attention',
            needsAttentionCount,
          )}
          {renderFilterButton('proposed', 'Proposed', proposedCount)}
          {renderFilterButton('accepted', 'Accepted', acceptedCount)}
          {renderFilterButton('ignored', 'Ignored', ignoredCount)}
          {renderFilterButton('all', 'All', drafts.length)}
        </Stack>

        <TableContainer
          sx={{
            border: '1px solid rgba(0,0,0,0.12)',
            borderRadius: '4px',
            maxHeight: '65vh',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 105 }}>Status</TableCell>
                <TableCell>Found Device</TableCell>
                <TableCell sx={{ width: 150 }}>Type</TableCell>
                <TableCell sx={{ width: 300 }}>Proposed Destination</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDrafts.map((draft) => {
                const candidate = candidates.find(
                  (item) =>
                    item.discoveredDeviceId === draft.discoveredDeviceId,
                );
                const selectedUnit =
                  units.find((unit) => unit._id === draft.unitId) ?? null;
                const candidateUnits = units.filter((unit) =>
                  unitMatchesCandidate(unit, candidate),
                );
                const status = reviewStatus(draft);
                const displayName =
                  candidate?.displayName ?? draft.discoveredDeviceId;
                const proposedDeviceName =
                  draft.proposedDeviceName ?? displayName;
                const expanded = expandedDeviceId === draft.discoveredDeviceId;

                return (
                  <React.Fragment key={draft.discoveredDeviceId}>
                    <TableRow hover>
                      <TableCell>
                        <Chip
                          color={reviewStatusColor(status)}
                          label={reviewStatusLabel(status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{displayName}</Typography>
                        <Typography color="text.secondary" variant="caption">
                          {draft.discoveredDeviceId}
                        </Typography>
                        <TextField
                          disabled={status === 'ignored'}
                          fullWidth
                          inputProps={{
                            'aria-label': `Super name for ${displayName}`,
                          }}
                          label="Super name"
                          margin="dense"
                          onChange={(event) =>
                            updateDraftName(
                              draft.discoveredDeviceId,
                              event.target.value,
                            )
                          }
                          size="small"
                          value={proposedDeviceName}
                        />
                        <Typography color="text.secondary" variant="caption">
                          {proposedNameEvidenceText(draft)}
                        </Typography>
                        <Typography
                          color={
                            draft.reason ? 'text.primary' : 'text.secondary'
                          }
                          sx={{ display: 'block', mt: 0.5 }}
                          variant="caption"
                        >
                          {mappingEvidenceText(draft)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {draft.deviceType}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          disabled={status === 'ignored'}
                          filterOptions={(options, state) => {
                            const search = state.inputValue
                              .trim()
                              .toLowerCase();
                            if (!search) {
                              return options;
                            }
                            return options.filter((unit) =>
                              unitSearchText(unit).includes(search),
                            );
                          }}
                          getOptionLabel={unitLabel}
                          isOptionEqualToValue={(option, value) =>
                            option._id === value._id
                          }
                          loading={unitsLoading}
                          noOptionsText={
                            propertyId
                              ? 'No matching units'
                              : 'Source property is not available'
                          }
                          onChange={(_event, unit) =>
                            updateDraftUnit(
                              draft.discoveredDeviceId,
                              unit ?? undefined,
                            )
                          }
                          options={candidateUnits}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {unitsLoading ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={18}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                              label={
                                isCentralEquipmentDevice(draft.deviceType)
                                  ? 'Assign to central equipment unit'
                                  : 'Assign to unit'
                              }
                            />
                          )}
                          size="small"
                          value={selectedUnit}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          <Tooltip title="Details">
                            <IconButton
                              aria-label={`View details for ${displayName}`}
                              onClick={() =>
                                setExpandedDeviceId(
                                  expanded ? null : draft.discoveredDeviceId,
                                )
                              }
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {status === 'ignored' ? (
                            <Tooltip title="Restore">
                              <IconButton
                                aria-label={`Restore ${displayName}`}
                                onClick={() =>
                                  restoreDraft(draft.discoveredDeviceId)
                                }
                                size="small"
                              >
                                <Undo fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title="Accept">
                                <span>
                                  <IconButton
                                    aria-label={`Accept ${displayName}`}
                                    disabled={
                                      !draft.unitId || status === 'accepted'
                                    }
                                    onClick={() =>
                                      acceptDraft(draft.discoveredDeviceId)
                                    }
                                    size="small"
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Ignore for now">
                                <IconButton
                                  aria-label={`Ignore ${displayName} for now`}
                                  onClick={() =>
                                    ignoreDraft(draft.discoveredDeviceId)
                                  }
                                  size="small"
                                >
                                  <Block fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {expanded ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Stack spacing={1}>
                            <Typography variant="subtitle2">
                              Device details
                            </Typography>
                            <Typography variant="body2">
                              Metrics:{' '}
                              {(candidate?.supportedMetrics ?? []).join(', ') ||
                                'None reported'}
                            </Typography>
                            <Typography variant="body2">
                              Commands:{' '}
                              {(candidate?.supportedCommands ?? []).join(
                                ', ',
                              ) || 'None reported'}
                            </Typography>
                            {draft.modelVotes?.length ? (
                              <Typography
                                color="text.secondary"
                                variant="body2"
                              >
                                AI providers returned {draft.modelVotes.length}{' '}
                                votes for this candidate.
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })}
              {!visibleDrafts.length ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" variant="body2">
                      No devices match this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        {unitError ? (
          <Alert severity="warning">
            Unable to load property units for mapping.
          </Alert>
        ) : null}
        {!readyToCommit && complete ? (
          <Alert severity="info">
            Accept proposed mappings or mark unresolved devices as ignored
            before committing. Conductor will not publish mapped devices until
            this step succeeds.
          </Alert>
        ) : null}
        <Box>
          <Button
            disabled={!readyToCommit || committing || committed}
            onClick={handleCommit}
            variant="contained"
          >
            {committed ? 'Mappings Committed' : 'Commit Mappings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
